import type { TargetsSaveFailure } from "./useTargetsDoc";

// Save-error copy for the targets editor, split out of TargetsEditor.tsx to
// keep it under the file-size cap (prompt.md §Limiti di dimensione). Pure
// data + one resolver: the ONE non-cosmetic change this move made was an
// em-dash in `missing_validator`'s defaultValue, fixed here (no-llmisms) and
// matched in all 5 locale JSONs' `coach.targets.errRetry` (the key that
// wins over this defaultValue at runtime whenever it exists, which is
// always outside a missing-translation fallback) so the visible text is the
// same comma phrasing everywhere, not just in the fallback nobody normally
// sees.
//
// This copy is written for the coach reading it about an ATHLETE's program
// (no_grant / not_coach / multi_assignee are coach-only-reachable failure
// modes, see useTargetsDoc). `not_found` is the one reason both paths can
// hit, and its coach phrasing names a "program": the self path edits a
// personal targets doc, never a program, so that word is wrong there (ME-3a).
const SAVE_ERROR_KEY: Record<TargetsSaveFailure, string> = {
  stale: "coach.targets.errStale",
  no_grant: "coach.targets.errNoGrant",
  not_coach: "coach.targets.errNotCoach",
  multi_assignee: "coach.targets.errMultiAssignee",
  missing_validator: "coach.targets.errRetry",
  not_found: "coach.targets.errNotFound",
  offline_or_unknown: "coach.targets.errOffline",
};

const SAVE_ERROR_DEFAULT: Record<TargetsSaveFailure, string> = {
  stale: "These targets changed while you were editing. Reload and re-apply.",
  no_grant: "The athlete hasn't granted you target edits.",
  not_coach: "You aren't this athlete's coach.",
  multi_assignee: "This program is shared by more than one athlete.",
  missing_validator: "Still loading, try again in a moment.",
  not_found: "This program no longer exists.",
  offline_or_unknown: "Couldn't save. Check your connection and try again.",
};

// Self-path sibling for `not_found` only (ME-3a): the other coach-labelled
// reasons above are unreachable when `source.athleteSub` is unset, so they
// have no self copy to leak.
const SAVE_ERROR_KEY_SELF_NOT_FOUND = "me.targets.errNotFound";
const SAVE_ERROR_DEFAULT_SELF_NOT_FOUND =
  "Couldn't find your targets. Go back and retry.";

/** `isSelf` = editing your OWN targets (no coach `athleteSub`, ME-3a). */
export function resolveTargetsSaveErrorCopy(
  reason: TargetsSaveFailure,
  isSelf: boolean,
): { key: string; defaultValue: string } {
  const useSelfCopy = isSelf && reason === "not_found";
  return useSelfCopy
    ? { key: SAVE_ERROR_KEY_SELF_NOT_FOUND, defaultValue: SAVE_ERROR_DEFAULT_SELF_NOT_FOUND }
    : { key: SAVE_ERROR_KEY[reason], defaultValue: SAVE_ERROR_DEFAULT[reason] };
}
